import { DateTime } from "luxon";
import { createStore } from "@stencil/store";
import {
  NotificationItem,
  NotificationPagination,
  NotificationSettingsChannel,
  NotificationSettingsNotificationType,
  NotificationsFlyout,
} from "api/model/notifications";
import NotificationsSettingsResponse from "@apis/responses/get-notifications-settings-response";
import NotificationsResponse from "@apis/responses/get-notifications.response";
import { FlyoutState } from "@apis/model/core";

import {
  notificationsDataUrl,
  notificationsFlyoutDataUrl,
  notificationsSettingsAccountDataUrl,
  notificationsSettingsDataUrl,
  notificationsSettingsSwitchDataUrl,
} from "../frontend-kit/organisms/f-header/f-notification-flyout/f-notification-config";

const headers = {
  "Content-Type": "application/json",
  Accept: "application/json",
};

interface notificationsStore {
  totalUnreadNotifications: number;
  notificationsFlyoutList: NotificationItem[];
  notificationsFlyoutMaxElements: number;
  notifications: NotificationItem[];
  notificationsSettingsChannels: NotificationSettingsChannel[];
  notificationsSettingsNotificationsTypes: NotificationSettingsNotificationType[];
  notificationsSettingsLocalNotifications: boolean;
  pagination: NotificationPagination;
  is: FlyoutState;
  loadingSettings: boolean;
}

const { state } = createStore({
  totalUnreadNotifications: null,
  notificationsFlyout: {},
  notificationsFlyoutList: [],
  notificationsFlyoutMaxElements: null,
  notifications: [],
  notificationsSettingsChannels: [],
  notificationsSettingsNotificationsTypes: [],
  notificationsSettingsLocalNotifications: false,
  pagination: null,
  is: "INIT",
  loadingSettings: false,
} as notificationsStore);

export const getNotificationsFlyout: () => void = async () => {
  state.is = "LOADING";
  return fetch(notificationsFlyoutDataUrl, {
    method: "GET",
    headers,
  })
    .then(async (response) => {
      if (!response.ok) {
        await handleErrors();
      }
      const notificationsFlyout: NotificationsFlyout = await response.json();
      state.notificationsFlyoutList = notificationsFlyout?.notifications;
      state.totalUnreadNotifications =
        notificationsFlyout?.totalUnreadNotifications;
      state.notificationsFlyoutMaxElements =
        notificationsFlyout?.limitBeforeScroll;
    })
    .catch(handleErrors)
    .finally(() => {
      state.is = "LOADED";
    });
};

export const countUnread = (read: boolean, numb?: number) => {
  const { totalUnreadNotifications } = state;
  let unread = totalUnreadNotifications.valueOf();
  if (read && numb) {
    unread = unread - numb;
  } else if (!numb) {
    unread = 0;
  } else {
    unread = unread + numb;
  }
  return unread;
};

export const mark = (codes, el, action) =>
  el.map((notification) => {
    if (codes.length) {
      const index = codes.findIndex((code) => notification.code === code);
      if (index > -1) {
        notification.read = action;
      }
    } else {
      notification.read = action;
    }
    return notification;
  });

export const animate = (code, el) =>
  el.map((notification) => {
    if (notification.code === code) {
      notification.deleted = true;
    }
    return notification;
  });

export const getIconName = (
  type: "ERROR" | "WARNING" | "INFO",
  isLegacy = false
) => {
  const iconObj = {
    ERROR: isLegacy ? "error" : "alert-error",
    WARNING: isLegacy ? "warning-filled" : "alert-info",
    INFO: isLegacy ? "alert-info-filled" : "alert-success",
  };
  return iconObj[type] ?? "";
};

export const setLocalTime = (utc, displayFormat) => {
  return DateTime.fromISO(utc).toLocal().toFormat(displayFormat);
};

export const markNotification: (
  codes: Array<string>,
  action: "read" | "unread"
) => void = async (codes, action) => {
  state.is = "LOADING";
  const body = (codes.length && { notifications: codes }) || undefined;
  return fetch(`${notificationsDataUrl}?action=${action}`, {
    method: "PUT",
    headers,
    body: JSON.stringify(body),
  })
    .then(async (response) => {
      if (!response.ok) {
        await handleErrors();
      }
      state.notificationsFlyoutList = mark(
        codes,
        state.notificationsFlyoutList,
        action === "read" ? true : false
      );
      state.notifications = mark(
        codes,
        state.notifications,
        action === "read" ? true : false
      );
      state.totalUnreadNotifications = countUnread(
        action === "read" ? true : false,
        codes.length
      );
    })
    .catch(handleErrors)
    .finally(() => (state.is = "LOADED"));
};

export const addNextUnreadNotification: () => void = async () => {
  const currentNotifications = state.notificationsFlyoutList;
  return fetch(notificationsFlyoutDataUrl, {
    method: "GET",
    headers,
  })
    .then(async (response) => {
      if (!response.ok) {
        await handleErrors();
      }
      const notificationsFlyout: NotificationsFlyout = await response.json();
      const currentCodes = currentNotifications.map(
        (notification) => notification.code
      );
      const nextNotification = notificationsFlyout.notifications.find(
        (notification) => {
          return !currentCodes.includes(notification.code);
        }
      );
      if (nextNotification) {
        state.notificationsFlyoutList = [
          ...currentNotifications,
          { ...nextNotification, added: true },
        ];
      }
    })
    .catch(handleErrors);
};

export const deleteNotification: (
  code?: string,
  isRead?: boolean
) => void = async (code: string | undefined, isRead: boolean | undefined) => {
  state.is = "LOADING";
  return fetch(`${notificationsDataUrl}${code ? "/" + code : ""}`, {
    method: "DELETE",
    headers,
  })
    .then(async (response) => {
      if (!response.ok) {
        await handleErrors();
      }
      const { pagination } = state;
      if (pagination) {
        /* eslint-disable */
        let { totalNumberOfResults, ...rest } = pagination;
        if (totalNumberOfResults) {
          if (code) {
            totalNumberOfResults -= 1;
          } else {
            totalNumberOfResults = 0;
          }
          state.pagination = { totalNumberOfResults, ...rest };
        }
      }
      if (code) {
        const notifications = state.notifications?.filter(
          (notification) => notification.code !== code
        );
        state.notifications = notifications;
        const notificationsFlyout = state.notificationsFlyoutList?.filter(
          (notification) => notification.code !== code
        );
        state.notificationsFlyoutList = notificationsFlyout;
        await addNextUnreadNotification();
      } else {
        state.notifications = [];
        state.notificationsFlyoutList = [];
      }
      if (!isRead) {
        state.totalUnreadNotifications = countUnread(
          true,
          Boolean(code) ? 1 : undefined
        );
      }
    })
    .catch(handleErrors)
    .finally(() => (state.is = "LOADED"));
};

export const getAllNotifications: () => void = async () => {
  state.is = "LOADING";
  return fetch(notificationsDataUrl, {
    method: "GET",
    headers,
  })
    .then(async (response) => {
      if (!response.ok) {
        await handleErrors();
      }
      const notificationsResponse: NotificationsResponse =
        await response.json();
      const notifications = notificationsResponse?.results ?? [];
      state.notifications = notifications;
      state.pagination = notificationsResponse?.pagination;
    })
    .catch(handleErrors)
    .finally(() => (state.is = "LOADED"));
};

export const getMoreNotifications: () => void = async () => {
  state.is = "LOADING";
  return fetch(
    `${notificationsDataUrl}?page=${state.pagination?.currentPage + 1 ?? 0}`,
    {
      method: "GET",
      headers,
    }
  )
    .then(async (response) => {
      if (!response.ok) {
        await handleErrors();
      }
      const notificationsResponse: NotificationsResponse =
        await response.json();
      const notifications = notificationsResponse?.results ?? [];
      state.pagination = notificationsResponse?.pagination;
      state.notifications = [...state.notifications, ...notifications];
    })
    .catch(handleErrors)
    .finally(() => (state.is = "LOADED"));
};

export const getNotificationsSettings: () => void = async () => {
  state.loadingSettings = true;
  return fetch(notificationsSettingsDataUrl, {
    method: "GET",
    headers,
  })
    .then(async (response) => {
      if (!response.ok) {
        await handleErrors();
      }
      const notificationsSettingsResponse: NotificationsSettingsResponse =
        await response.json();
      const channels = notificationsSettingsResponse?.types ?? [];
      const notificationTypes = notificationsSettingsResponse?.items ?? [];
      const localNotifications =
        notificationsSettingsResponse?.localNotifications ?? false;
      state.notificationsSettingsChannels = channels;
      state.notificationsSettingsNotificationsTypes = notificationTypes;
      state.notificationsSettingsLocalNotifications = localNotifications;
    })
    .catch(handleErrors)
    .finally(() => (state.loadingSettings = false));
};

export const setNotificationSetting: (
  optionId: string
) => Promise<void> = async (optionId: string) => {
  return fetch(`${notificationsSettingsSwitchDataUrl}/${optionId}`, {
    method: "PUT",
    headers,
  }).then(async (response) => {
    if (!response.ok) {
      await handleErrors();
    }
    state.notificationsSettingsChannels = [
      ...state.notificationsSettingsChannels,
    ];
    state.notificationsSettingsNotificationsTypes = switchToggle(
      optionId,
      state.notificationsSettingsNotificationsTypes
    );
  });
};

const switchToggle = (id, el) =>
  el.map((notificationSettingsTypes) => {
    const channels = state.notificationsSettingsChannels;
    channels.forEach((channel) => {
      if (notificationSettingsTypes[channel.id].id === id) {
        notificationSettingsTypes[channel.id].selected =
          !notificationSettingsTypes[channel.id].selected;
      }
    });
    return notificationSettingsTypes;
  });

export const setNotificationSettingAccount: () => Promise<void> = async () => {
  return fetch(`${notificationsSettingsAccountDataUrl}`, {
    method: "PUT",
    headers,
  }).then(async (response) => {
    if (!response.ok) {
      await handleErrors();
    }
    state.notificationsSettingsLocalNotifications =
      !state.notificationsSettingsLocalNotifications;
  });
};

const handleErrors = async () => {
  state.is = "ERROR";
};

export const animateNotification = (ev: any) => {
  state.notificationsFlyoutList = animate(
    ev.detail,
    state.notificationsFlyoutList
  );
};

export default state;
